import { useMediaQuery } from '@vueuse/core';

const MOBILE_UA_PATTERN = /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i;

// SSR has no `window`, so the media query can't resolve — fall back to user-agent sniffing so the
// server renders the right state for the device (desktop: persisted cookie value; mobile: closed
// drawer). The detected value is stashed in `useState` so SPA navigations on the client keep the
// same answer instead of re-reading headers (which are empty on the client).
const useIsMobileUserAgent = (): Ref<boolean> => useState<boolean>('sidebar-is-mobile-ua', () => {
  if (import.meta.client) {
    return false;
  }
  const ua = useRequestHeaders(['user-agent'])['user-agent'] ?? '';
  return MOBILE_UA_PATTERN.test(ua);
});

export const useSidebarOpenState = (cookieKey: string) => {
  const desktopOpen = useClusterCookie<boolean>(cookieKey, {
    default: () => true,
    maxAge: 60 * 60 * 24 * 365,
  });

  const mobileOpen = ref(false);
  const isMobileQuery = useMediaQuery('(max-width: 767px)');
  const isMobileUA = useIsMobileUserAgent();

  const mounted = ref(false);
  onMounted(() => {
    mounted.value = true;
  });

  // UA-derived value drives SSR + initial hydration (so the markup matches the device); the live
  // media query takes over once mounted (so resizing or a tablet that misses the UA test still
  // works). When the two agree (the common case), hydration is animation-free; when they don't,
  // `KUICoreLayoutSidebar`'s no-transition initial render swallows the snap.
  const isMobile = computed(() => (mounted.value ? isMobileQuery.value : isMobileUA.value));

  const open = computed({
    get: () => (isMobile.value ? mobileOpen.value : desktopOpen.value),
    set: (value: boolean) => {
      if (isMobile.value) {
        mobileOpen.value = value;
      }
      else {
        desktopOpen.value = value;
      }
    },
  });

  const route = useRoute();
  watch(() => route.fullPath, () => {
    if (isMobile.value) {
      mobileOpen.value = false;
    }
  });

  return { open, isMobile, mounted };
};
